import { useRef, useState } from "react";

import { Icon, IconSize, IconVariant } from "../../../Atoms/Icons/Icon/Icon";
import { useClickOutside } from "../../../Common/Hooks/useClickOutside";
import { DropdownContainer } from "./DropdownContainer";

import styles from "./DropdownButton.module.scss";

import ChevronDownRegularIcon from "../../../Atoms/Icons/Types/ChevronDown/chevron-down-regular.svg";

type DropdownButtonSize = "xs" | "sm" | "md" | "lg";
type DropdownButtonCategory = "primary" | "secondary" | "tertiary";
type DropdownButtonDirection = "bottomRight" | "topRight" | "bottomLeft" | "topLeft";

interface IDropdownButtonProps {
    /**
     * The optional button ID.
     */
    id?: string;

    /**
     * The label of the main button.
     */
    label: string;

    /**
     * The button color scheme.
     */
    variant?: IconVariant;

    /**
     * The category of importance.
     */
    category?: DropdownButtonCategory;

    /**
     * The size of the icon and buttons.
     */
    size?: DropdownButtonSize;

    /**
     * The direction where the dropdown will open.
     */
    direction?: DropdownButtonDirection;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * Whether to disable the button click event.
     */
    disabled?: boolean;

    /**
     * Whether to disable the dropdown toggle click event.
     */
    toggleDisabled?: boolean;

    /**
     * An array with all items to see when the dropdown is open
     */
    items: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        iconButton?: JSX.Element;
    }[];

    /**
     * A callback to invoke when the button is clicked.
     */
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const variantMapper: Record<IconVariant, string> = {
    "brand": styles.brand,
    "error": styles.error,
    "neutral": styles.neutral,
    "success": styles.success,
    "warning": styles.warning
} as const;

const categoryMapper: Record<DropdownButtonCategory, string> = {
    "primary": styles.primary,
    "secondary": styles.secondary,
    "tertiary": styles.tertiary
} as const;

const dropdownIconSizeMapper = {
    "lg": "md" as IconSize,
    "md": "sm" as IconSize,
    "sm": "xs" as IconSize,
    "xs": "xxs" as IconSize
};

/**
 * The dropdown button.
 * @param {IDropdownButtonProps} param The props used to render the dropdown button.
 * @returns The rendered component.
 */
function DropdownButton({
    id,
    label,
    variant = "brand",
    category = "primary",
    direction = "bottomRight",
    size = "md",
    dataCy,
    disabled,
    toggleDisabled,
    items,
    onClick
}: Readonly<IDropdownButtonProps>): JSX.Element {
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);

    useClickOutside([dropdownRef, targetRef], () => setShowDropdown(false));

    return (
        <div
            className={styles.container}
            ref={targetRef}
        >
            <div
                className={`${styles.buttonsContainer} ${styles[size]}`}
                data-cy={dataCy}
                id={id}
            >
                <button
                    onClick={e => { onClick?.(e); setShowDropdown(false); }}
                    disabled={disabled}
                    className={`${styles.mainButton} ${variantMapper[variant]} ${categoryMapper[category]} ${styles[size]}`}
                    data-cy={`${dataCy}-mainButton`}
                >
                    {label}
                </button>
                <div
                    className={`${styles.separator} ${variantMapper[variant]} ${categoryMapper[category]} ${disabled ? styles.disabled : ""}`}
                />
                <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    disabled={toggleDisabled}
                    className={`${styles.dropdownButton} ${variantMapper[variant]} ${categoryMapper[category]} ${styles[size]}`}
                    data-cy={`${dataCy}-dropdownButton`}
                >
                    <Icon size={dropdownIconSizeMapper[size]}>
                        <ChevronDownRegularIcon />
                    </Icon>
                </button>
            </div>
            {showDropdown && (
                <DropdownContainer
                    dropdownRef={dropdownRef}
                    targetRef={targetRef}
                    size={size}
                    direction={direction}
                    items={items}
                    onItemClickCallback={item => {
                        item.onClick();
                        setShowDropdown(false);
                    }}
                />
            )}
        </div>
    );
}

export { DropdownButton, DropdownButtonCategory, DropdownButtonDirection, DropdownButtonSize, IDropdownButtonProps };
